import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import BlockBuilder from '@repo/ui-components/sanity/BlockBuilder';
import BreadcrumbWithWrapper from '@repo/ui-components/BreadcrumbWithWrapper';
import Hero from '@repo/ui-components/hero/Hero';
import PageWrapper from '@repo/ui-components/page-wrapper/PageWrapper';
import sharedStyles from '@repo/ui-components/styles';

import createOpenGraphTags from '@repo/utils/createOpenGraphTags';

import Layout from '@components/layout';
import getInternalLink from '@components/utils/getInternalLink';
import { PageHeading } from '@components/typography';
import GearTagLine from '@components/GearTagLine';
import headerImage from '@images/sportdog-about-us-press-release.jpg';
import { getLocalizedPagePath } from '@repo/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';
import formatDateForPressLink from '../utils/formatDateForPressLink';

const Article = styled.article`
  position: relative;

  header {
    margin-bottom: ${sharedStyles.spacing['spacing-lg']};
  }

  footer {
    margin-top: ${sharedStyles.spacing['spacing-lg']};
  }
`;

const DateBox = styled.div`
  display: block;
  font-weight: 700;
`;

function PressRoom({ pageContext }) {
  const {
    SEOMetadata,
    title,
    description,
    publishedDate,
    body,
    linkHreflangValues,
    slug,
  } = pageContext;

  const { tt, locale } = useSportdogInternationalContext();

  const year = new Date(publishedDate).getFullYear();

  const breadcrumbs = [
    { link: `/`, text: tt(s => s.breadcrumbsCommon.home) },
    {
      link: `/about-us/press-room`,
      text: tt(s => s.breadcrumbsCommon.pressRoom),
    },
    {
      link: `/about-us/press-room/${year}`,
      text: tt(s => s.breadcrumbsCommon.pressReleases, { year }),
    },
    {
      link: `/about-us/press-room/releases/${slug.current}`,
      text: tt(s => s.breadcrumbsCommon.pressRelease),
    },
  ];

  return (
    <Layout
      headTagData={{
        title: SEOMetadata?.title || title,
        description: SEOMetadata?.description || description,
        canonicalUrl: SEOMetadata?.canonicalUrl,
        noIndex: SEOMetadata?.noIndex,
        noFollow: SEOMetadata?.noFollow,
        meta: createOpenGraphTags(
          {
            title: SEOMetadata?.ogTitle || SEOMetadata?.title || title,
            description:
              SEOMetadata?.ogDescription ||
              SEOMetadata?.description ||
              description,
            url:
              SEOMetadata?.ogUrl ||
              SEOMetadata?.canonicalUrl ||
              (typeof window !== 'undefined' && window?.location?.pathname) ||
              getLocalizedPagePath('/', locale),
            image: SEOMetadata?.ogImage || '',
          },
          'sportdog'
        ),
      }}
      linkHreflangValues={linkHreflangValues}
    >
      <BreadcrumbWithWrapper elements={breadcrumbs} />

      <PageWrapper>
        <Hero
          hero={{
            mediaItem: headerImage,
            heroType: 'image',
            headline1: tt(s => s.pressRoomDetailPage.heroHeadline1),
            altText: tt(s => s.pressRoomDetailPage.heroAltText),
            textAlignment: 'middleRight',
            mobileCrop: '31% 0',
          }}
          getInternalLink={getInternalLink}
        />

        <Article>
          <header>
            <PageHeading color="white">{title}</PageHeading>
            <DateBox>{formatDateForPressLink(publishedDate, locale)}</DateBox>
          </header>
          <BlockBuilder blocks={body} />
          <footer>
            <p>
              {tt(s => s.pressRoomDetailPage.footerParagraph, {
                aboutSportdogBrand: (
                  <strong>
                    {tt(s => s.pressRoomDetailPage.aboutSportdogBrand)}
                  </strong>
                ),
              })}
            </p>
          </footer>
        </Article>
      </PageWrapper>
      <GearTagLine />
    </Layout>
  );
}

PressRoom.propTypes = {
  pageContext: PropTypes.shape({
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    SEOMetadata: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      canonicalUrl: PropTypes.string,
      noIndex: PropTypes.bool,
      noFollow: PropTypes.bool,
      ogTitle: PropTypes.string,
      ogDescription: PropTypes.string,
      ogUrl: PropTypes.string,
      ogImage: PropTypes.shape({
        asset: PropTypes.shape({
          url: PropTypes.string,
        }),
      }),
    }),
    body: PropTypes.arrayOf(PropTypes.shape({})),
    blocks: PropTypes.arrayOf(PropTypes.shape({})),
    publishedDate: PropTypes.string.isRequired,
    linkHreflangValues: PropTypes.arrayOf(PropTypes.shape({})),
    slug: PropTypes.shape({
      current: PropTypes.string,
    }),
  }).isRequired,
};
export default PressRoom;
